import { IdentifierDTO } from '@zazume/zzm-base';

import { PaginationOptions } from '#/hooks';
import { BasicUnit } from '#/models';
import { Invoice, RelatedModelType } from '#/models/invoice/Invoice';
import { SendInvoiceEmailInputs } from '#/pages/accounting/invoices/components/SendInvoiceEmail.schema';
import { ExportInvoicesInputs } from '#/pages/accounting/invoices/tabs/issued/components/ExportInvoices.schema';
import { AttachExternalInvoiceRequest } from '#lib/network/api/invoices/requests/AttachExternalInvoiceRequest';
import {
  doDelete,
  doGet,
  doPost,
  doPostFileUpload,
  doPut,
  EndpointBuilder,
  toPaginatedURL
} from '#lib/network/helpers';

export const invoices = {
  get: (invoiceId: IdentifierDTO) => () => doGet<Invoice>(`/dashboard/invoices/${invoiceId}`),
  getFromDocumentId: (documentId: IdentifierDTO) => () =>
    doGet<Invoice>(`/dashboard/invoices/from-document/${documentId}`),
  create: (body: object): Promise<Invoice> => doPost('/dashboard/invoices/create', body),
  update: ({
    relatedModelId,
    relatedModelType
  }: {
    relatedModelId: IdentifierDTO;
    relatedModelType: RelatedModelType;
  }): Promise<Invoice> => doPut(`/dashboard/invoices/${relatedModelType}/${relatedModelId}`),
  sendByEmail: (body: SendInvoiceEmailInputs, invoiceId: IdentifierDTO) =>
    doPost(`/dashboard/invoices/${invoiceId}/send-email`, body),
  export: (body: ExportInvoicesInputs, organizationId: IdentifierDTO) =>
    doPost(`/dashboard/invoices/export/${organizationId}`, body),
  exportReceived: (body: ExportInvoicesInputs, organizationId: IdentifierDTO) =>
    doPost(`/dashboard/invoices/export/received/${organizationId}`, body),
  exportIssued: (body: ExportInvoicesInputs, organizationId: IdentifierDTO) =>
    doPost(`/dashboard/invoices/export/issued/${organizationId}`, body),
  findUnitsWithReceivedInvoices: (organizationId: IdentifierDTO) => () =>
    doGet<BasicUnit[]>(`/dashboard/invoices/units/organization/${organizationId}/received`),
  findUnitsWithIssuedInvoices: (organizationId: IdentifierDTO) => () =>
    doGet<BasicUnit[]>(`/dashboard/invoices/units/organization/${organizationId}/issued`),
  attachExternalInvoice: (request: AttachExternalInvoiceRequest) =>
    doPostFileUpload<void>('/dashboard/invoices/external', request.build()),
  fromExpense: (expenseId: IdentifierDTO) => () => doGet<Invoice[]>(`/dashboard/invoices/from-expense/${expenseId}`),
  fromIncome: (incomeId: IdentifierDTO) => () => doGet<Invoice[]>(`/dashboard/invoices/from-income/${incomeId}`),
  deleteExternalInvoice: (invoiceId: IdentifierDTO) => doDelete(`/dashboard/invoices/external/${invoiceId}`),
  findIssued: (organizationId: IdentifierDTO, params: EndpointBuilder) => (pagination: PaginationOptions) => () =>
    doGet<Invoice[]>(
      toPaginatedURL(`/dashboard/invoices/organization/${organizationId}/issued${params.queryParams}`, pagination)
    ),
  findReceived: (organizationId: IdentifierDTO, params: EndpointBuilder) => (pagination: PaginationOptions) => () =>
    doGet<Invoice[]>(
      toPaginatedURL(`/dashboard/invoices/organization/${organizationId}/received${params.queryParams}`, pagination)
    ),
  createCreditNote: () => (invoiceId: IdentifierDTO) => doPost(`/dashboard/invoices/${invoiceId}/credit-note`),
  createFiskalyRemedy: () => (invoiceId: IdentifierDTO) => doPost(`/dashboard/invoices/${invoiceId}/remedy`)
};
